import { useState } from 'react';
import { AxiosInstance, AxiosError } from 'axios';
import { QueryClient } from '@tanstack/react-query';
import { retry } from './retry';
import { UseFormSetError } from 'react-hook-form';
import mapFormErrors from './mapFormErrors';
import { ErrorFlash, SuccessFlash } from '../../components/helper/flash-notification';

interface RequestOptions {
  endpoint: string;
  payload?: any;
  params?: any;
  refreshKeys?: any[];
  setError?: UseFormSetError<any>;
  silent?: boolean;
  filename?: string; // for download
}

export function useApiEngine(config: {
  client: AxiosInstance;
  queryClient: QueryClient;
  toast?: { success: (m: string) => void; error: (m: string) => void };
}) {
  const { client, queryClient, toast } = config;

  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]>>({});
  const [error, setError] = useState<any>(null);

  // ------------------------------
  // Centralized error handler
  // ------------------------------
  const handleError = (err: AxiosError<any>, setErrorFn?: UseFormSetError<any>, silent?: boolean) => {
    const data = err.response?.data;

    if (data?.errors) {
      setFormErrors(data.errors);
      if (setErrorFn) mapFormErrors(data.errors, setErrorFn);
    }

    setError(data);
    if (!silent) ErrorFlash(data?.message || 'Request failed');
  };

  // ------------------------------
  // POST mutation (per-request)
  // ------------------------------
  const POST = async ({
    endpoint,
    payload,
    refreshKeys = [],
    setError: setErrorFn,
    silent,
  }: RequestOptions) => {
    setPostLoading(true);
    setFormErrors({});
    setError(null);

    try {
      const res = await retry(() =>
        client.post(endpoint, payload).then(r => r.data)
      );

      refreshKeys.forEach((key) => {
        queryClient.invalidateQueries({
          queryKey: Array.isArray(key) ? key : [key],
        });
      });

      if (!silent) SuccessFlash(res?.message || 'Success');
      return res;
    } catch (err: any) {
      handleError(err, setErrorFn, silent);
      return null;
    } finally {
      setPostLoading(false);
    }
  };


  // ------------------------------
  // DELETE mutation
  // ------------------------------
  const DELETE = async ({ endpoint, params, refreshKeys = [], silent }: RequestOptions) => {
    setLoading(true);
    setError(null);

    try {
      const res = await retry(() => client.delete(endpoint, { params }).then(r => r.data));
      refreshKeys.forEach((key) => {
        queryClient.invalidateQueries({
          queryKey: Array.isArray(key) ? key : [key],
        });
      });
      if (!silent) toast?.success(res?.message || 'Deleted successfully');
      return res;
    } catch (err: any) {
      handleError(err, undefined, silent);
      return null;
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // GET request
  // ------------------------------
  const GET = async ({ endpoint, params, silent }: RequestOptions) => {
    setLoading(true);
    setError(null);

    try {
      const res = await retry(() => client.get(endpoint, { params }).then(r => r.data));
      if (!res) {
        return null;
      }
      return res.data || res;
    } catch (err: any) {
      handleError(err, undefined, silent);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // ------------------------------
  // DOWNLOAD file
  // ------------------------------
  const DOWNLOAD = async ({ endpoint, params, filename = 'file', silent }: RequestOptions) => {
    setLoading(true);
    setError(null);

    try {
      const res = await client.get(endpoint, { params, responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      if (!silent) toast?.success('Download started');
    } catch (err: any) {
      handleError(err, undefined, silent);
    } finally {
      setLoading(false);
    }
  };

  return { POST, GET, DELETE, DOWNLOAD, loading, postLoading, error, formErrors };
}
